import { useLocation } from "react-router-dom";
import { useMemo } from "react";
import useMenuItems from "./useMenuItems";
import type { MenuItem } from "@/types";

export interface Crumb { label: string; to?: string; }

/**
 * Resolves the href for a GROUP-level parent crumb (a top-level menu item
 * that itself has no `to`, e.g. Settings, Deployment, Workflows, Reports,
 * Billing - only Integrations and Knowledge Base have a parent `to`).
 *
 * Never falls back to the current page's own path - that produces a dead
 * link (the SPA router sees no URL change, so clicking does nothing).
 * Preference order:
 *   1. The item's own `to`, if it's not the current page.
 *   2. A sibling sublink whose label looks like a "hub"/landing page for the
 *      section (e.g. "Settings Hub"), if it's not the current page.
 *   3. undefined - there is genuinely no distinct parent page to link to,
 *      so the caller renders plain (non-clickable) text instead of a
 *      same-URL link.
 */
function resolveParentHref(item: MenuItem, currentPath: string): string | undefined {
  if (item.to && item.to !== currentPath) return item.to;

  const hub = item.sublinks?.find(
    s => s.enabled === true && s.to && s.to !== currentPath && /\bhub\b/i.test(s.label)
  );
  if (hub?.to) return hub.to;

  return undefined;
}

export function useBreadcrumb(): Crumb[] {
  const location = useLocation();
  const menuItems = useMenuItems();
  return useMemo(() => {
    const path = location.pathname;
    if (!path || path === "/") return [];
    const crumbs: Crumb[] = [];
    for (const item of menuItems) {
      if (!item.enabled) continue;
      if (item.sublinks) {
        // Respect `end`: an exact-match-only sublink (e.g. "All Integrations",
        // to="/integrations", end=true) must never prefix-match a route that
        // isn't actually in the menu (e.g. /integrations/callrail) - that
        // mislabels the trail with the wrong leaf crumb. `end` sublinks only
        // match their own exact path; everything else keeps prefix matching.
        const matches = item.sublinks.filter(sub => {
          if (!sub.enabled) return false;
          if (path === sub.to) return true;
          if (sub.end) return false;
          return path.startsWith(sub.to + "/");
        });
        if (matches.length > 0) {
          const best = matches.sort((a, b) => (b.to?.length ?? 0) - (a.to?.length ?? 0))[0];
          crumbs.push({ label: item.label, to: resolveParentHref(item, path) });
          crumbs.push({ label: best.label, to: best.to });
          break;
        }
      } else if (item.to && (path === item.to || path.startsWith(item.to + "/"))) {
        crumbs.push({ label: item.label, to: item.to });
        break;
      }
    }
    return crumbs;
  }, [location.pathname, menuItems]);
}
